import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import Button from '@/components/base/Button';
import { useAuth } from '@/context/AuthContext';
import {
  USAGE_CHANGED,
  formatNoticeTime,
  loadUsageNotices,
  noticeSeenKey,
  type UsageNotice,
} from '@/lib/usageNotifications';

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  /** 外部 / 待开发智能体入口 */
  external?: boolean;
}

/** 对照 demo SiteHeader.NAV_ITEMS，路径映射到本项目落地路由 */
const NAV_ITEMS: NavItem[] = [
  { to: '/', label: '首页', end: true },
  { to: '/leads', label: '潜在线索池' },
  { to: '/coming-soon/thinktank', label: '行业智库' },
  { to: '/coming-soon/bp', label: '税务合规BP工作台' },
  { to: '/coming-soon/boss', label: 'BOSS经营智能体' },
  { to: '/coming-soon/orders', label: '价格与权益' },
];

const ACCOUNT_ITEMS = [
  { to: '/my-analysis', label: '我的分析', icon: 'ri-file-chart-line' },
  { to: '/my/favorites', label: '我的收藏', icon: 'ri-bookmark-line' },
  { to: '/my/clients', label: '我的客户', icon: 'ri-contacts-book-2-line' },
  { to: '/coming-soon/reports', label: '我的报告', icon: 'ri-file-text-line' },
  { to: '/coming-soon/orders', label: '订单与权益', icon: 'ri-wallet-3-line' },
  { to: '/coming-soon/settings', label: '账号设置', icon: 'ri-settings-3-line' },
];

function maskPhone(value: string | undefined): string {
  if (!value) return '未绑定';
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) return `${digits.slice(0, 3)}****${digits.slice(7)}`;
  return value;
}

function useClickOutside(onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);
  return ref;
}

export default function Navbar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, signOut, requestLogin, openLogin } = useAuth();
  const isLoggedIn = Boolean(user);

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [notices, setNotices] = useState<UsageNotice[]>([]);
  const [noticeStatus, setNoticeStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [seenAt, setSeenAt] = useState(0);

  const isOverlayPage = pathname === '/';
  const solid = !isOverlayPage || scrolled;

  const msgRef = useClickOutside(() => setMsgOpen(false));
  const userRef = useClickOutside(() => setUserOpen(false));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const refreshNotices = useCallback(async () => {
    if (!user) {
      setNotices([]);
      setNoticeStatus('idle');
      return;
    }
    setNoticeStatus('loading');
    try {
      setNotices(await loadUsageNotices());
      setNoticeStatus('idle');
    } catch {
      setNoticeStatus('error');
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setSeenAt(0);
      setNotices([]);
      return;
    }
    const raw = Number(localStorage.getItem(noticeSeenKey(user.id)));
    setSeenAt(Number.isFinite(raw) ? raw : 0);
    void refreshNotices();
  }, [user, refreshNotices]);

  useEffect(() => {
    const onUsage = () => {
      void refreshNotices();
    };
    window.addEventListener(USAGE_CHANGED, onUsage);
    return () => window.removeEventListener(USAGE_CHANGED, onUsage);
  }, [refreshNotices]);

  useEffect(() => {
    if (!msgOpen || !user || notices.length === 0) return;
    const latest = notices.reduce((max, item) => Math.max(max, Date.parse(item.at) || 0), 0);
    if (latest <= seenAt) return;
    localStorage.setItem(noticeSeenKey(user.id), String(latest));
    setSeenAt(latest);
  }, [msgOpen, notices, seenAt, user]);

  useEffect(() => {
    setMobileOpen(false);
    setMsgOpen(false);
    setUserOpen(false);
  }, [pathname]);

  const hasUnread = notices.some((item) => (Date.parse(item.at) || 0) > seenAt);

  // 中间输入页已删除：分析入口直接回首页（首页即唯一的搜索/发起入口）
  const startUsing = () => {
    if (isLoggedIn) {
      navigate('/');
    } else {
      requestLogin(() => navigate('/'));
    }
  };

  const navIdleClass = solid
    ? 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
    : 'text-background-100 hover:bg-background-50/10 hover:text-background-50';

  return (
    <header
      className={`fixed inset-x-0 top-0 z-[60] transition-colors duration-300 ${
        solid
          ? 'border-b border-background-200 bg-background-50/95 backdrop-blur'
          : 'border-b border-transparent bg-transparent'
      }`}
    >
      <div className="flex h-16 items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2.5 whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-600 text-background-50">
            <i className="ri-shield-flash-line text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-base font-bold tracking-tight ${
                solid ? 'text-foreground-900' : 'text-background-50'
              }`}
            >
              税务洞察
            </span>
            <span
              className={`mt-0.5 text-[11px] font-medium tracking-wide ${
                solid ? 'text-primary-700' : 'text-accent-200'
              }`}
            >
              TaxInsight
            </span>
          </span>
        </Link>

        <nav className="ml-6 hidden items-center gap-1 lg:flex">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3 py-2 text-[13px] font-medium transition-colors ${
                  isActive ? 'bg-primary-100 text-primary-800' : navIdleClass
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 md:gap-3">
          <div className="relative" ref={msgRef}>
            <button
              type="button"
              onClick={() => setMsgOpen((v) => !v)}
              aria-label="消息"
              className={`relative flex h-9 w-9 items-center justify-center rounded-md transition-colors cursor-pointer ${
                solid
                  ? 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                  : 'text-background-100 hover:bg-background-50/10 hover:text-background-50'
              }`}
            >
              <i className="ri-notification-3-line text-lg" />
              {hasUnread ? (
                <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-accent-500" />
              ) : null}
            </button>
            {msgOpen ? (
              <div className="absolute right-0 mt-2 w-80 rounded-lg border border-background-200 bg-background-50 p-2 ti-rise">
                <div className="flex items-center justify-between px-2 py-1.5">
                  <span className="text-sm font-semibold text-foreground-900">消息</span>
                  <span className="text-xs text-foreground-400">与你的操作同步</span>
                </div>
                {!isLoggedIn ? (
                  <div className="px-2 py-4 text-center">
                    <p className="text-xs leading-relaxed text-foreground-500">
                      登录后显示你的分析、收藏和客户记录
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setMsgOpen(false);
                        openLogin();
                      }}
                      className="mt-3 inline-flex h-11 cursor-pointer items-center rounded-md bg-primary-600 px-3 text-sm text-background-50"
                    >
                      登录
                    </button>
                  </div>
                ) : noticeStatus === 'loading' && notices.length === 0 ? (
                  <p className="px-2 py-4 text-center text-xs text-foreground-500">正在同步…</p>
                ) : noticeStatus === 'error' && notices.length === 0 ? (
                  <p className="px-2 py-4 text-center text-xs text-foreground-500">消息暂时无法加载</p>
                ) : notices.length === 0 ? (
                  <p className="px-2 py-4 text-center text-xs leading-relaxed text-foreground-500">
                    还没有消息。完成分析、收藏线索或加入客户后会显示在这里。
                  </p>
                ) : (
                  notices.map((item) => (
                    <Link
                      key={item.id}
                      to={item.to}
                      onClick={() => setMsgOpen(false)}
                      className="flex items-start gap-3 rounded-md p-2 hover:bg-background-100"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                        <i className={`${item.icon} text-base`} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-medium text-foreground-900">
                          {item.title}
                        </span>
                        <span className="block text-xs leading-relaxed text-foreground-500">
                          {item.desc}
                        </span>
                        <span className="mt-0.5 block text-[11px] text-foreground-400">
                          {formatNoticeTime(item.at)}
                        </span>
                      </span>
                    </Link>
                  ))
                )}
              </div>
            ) : null}
          </div>

          {isLoggedIn ? (
            <div className="relative" ref={userRef}>
              <button
                type="button"
                onClick={() => setUserOpen((v) => !v)}
                className="flex cursor-pointer items-center gap-2 rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 transition-colors hover:border-primary-400"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-xs font-semibold text-background-50">
                  U
                </span>
                <span className="hidden text-[13px] font-medium text-foreground-800 md:inline">
                  {maskPhone(user?.phoneDigits || user?.phone)}
                </span>
                <i className="ri-arrow-down-s-line text-foreground-400" />
              </button>
              {userOpen ? (
                <div className="absolute right-0 mt-2 w-72 rounded-lg border border-background-200 bg-background-50 p-2 ti-rise">
                  <div className="px-2 py-1.5">
                    <span className="block text-[13px] font-semibold text-foreground-900">当前账号</span>
                    <span className="block text-xs text-foreground-500">
                      账号 · {maskPhone(user?.phoneDigits || user?.phone)}
                    </span>
                  </div>
                  <div className="my-1 h-px bg-background-200" />
                  <Link
                    to="/coming-soon/orders"
                    className="flex items-center justify-between rounded-md bg-primary-100/70 px-2 py-2 text-[13px] font-medium text-primary-800 hover:bg-primary-100"
                  >
                    <span className="flex items-center gap-2.5">
                      <i className="ri-vip-crown-2-line text-base" />
                      升级 / 查看价格与权益
                    </span>
                    <i className="ri-arrow-right-s-line text-base" />
                  </Link>
                  <div className="my-1 h-px bg-background-200" />
                  {ACCOUNT_ITEMS.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="flex items-center gap-2.5 rounded-md px-2 py-2 text-[13px] text-foreground-700 hover:bg-background-100"
                    >
                      <i className={`${item.icon} text-base text-foreground-500`} />
                      {item.label}
                    </Link>
                  ))}
                  <div className="my-1 h-px bg-background-200" />
                  <button
                    type="button"
                    onClick={async () => {
                      setUserOpen(false);
                      await signOut();
                      navigate('/');
                    }}
                    className="flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-left text-[13px] text-foreground-700 hover:bg-background-100"
                  >
                    <i className="ri-logout-box-r-line text-base text-foreground-500" />
                    退出登录
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <Button
              variant="outline"
              size="md"
              icon="ri-user-line"
              onClick={() => openLogin()}
              className="hidden sm:inline-flex"
            >
              登录
            </Button>
          )}

          <Button size="md" onClick={startUsing} className="hidden sm:inline-flex">
            开始使用
          </Button>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="菜单"
            className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-md lg:hidden ${
              solid
                ? 'text-foreground-700 hover:bg-background-100'
                : 'text-background-50 hover:bg-background-50/10'
            }`}
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
          </button>
        </div>
      </div>

      {mobileOpen ? (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
          <nav className="flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2.5 text-sm font-medium ${
                    isActive ? 'bg-primary-100 text-primary-800' : 'text-foreground-700'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-3 flex gap-2">
            {!isLoggedIn ? (
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => openLogin()}
                icon="ri-user-line"
              >
                登录
              </Button>
            ) : null}
            <Button className="flex-1" onClick={startUsing}>
              开始使用
            </Button>
          </div>
        </div>
      ) : null}
    </header>
  );
}
